import { Bus } from "baconjs"

import {
    NumberValue,
    Product,
    ProductResult,
    valueOf
} from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"

import { Events } from "../../constants"
import { Constants } from "./constants"
import { getProductInfo } from "./helper"
import {
    IVisualizationExtension3DExporter,
    IVisualizationOption,
    IVisualizationServer
} from "./visualizationService.types"

const getNumericValue = (
    product: Product | ProductResult,
    characteristic: string
) => {
    const numValue = valueOf(product, characteristic) as NumberValue
    return numValue?.value
}

export function generateAR(
    vis: IVisualizationServer,
    option: IVisualizationOption,
    eventBus: Bus<any>,
    productId: string
) {
    const cloud = vis.cloud
    cloud.addPendingEvent(Constants.PendingEvent.ExportScene)

    uploadToARServer(vis, option, productId)
        .then((response) => {
            response !== undefined &&
                eventBus.push({
                    type: Events.ShowARDialog,
                    link: response.link,
                    qr: response.qr
                })
        })
        .catch((err) => {
            console.error("Request to the AR service has failed", err)
            eventBus.push({
                type: Events.ShowError,
                message: L10n.format("Application.Visualization.AR.error")
            })
        })
        .finally(() =>
            cloud.removePendingEvent(Constants.PendingEvent.ExportScene)
        )
}

async function uploadToARServer(
    vis: IVisualizationServer,
    option: IVisualizationOption,
    productId: string
) {
    const product = await getProductInfo(vis, productId)
    if (!product) {
        return
    }

    const placement =
        valueOf(product, Constants.Characteristic.ARPlacement) ??
        option.placement ??
        "floor"
    const scaleFactor =
        getNumericValue(product, Constants.Characteristic.ARScaleFactor) ??
        option.scaleFactor ??
        1
    const retentionTime =
        getNumericValue(product, Constants.Characteristic.ARRetentionTime) ??
        option.retentionTime ??
        1

    const rotation = {
        x: option.rotation?.x ?? 0,
        y: option.rotation?.y ?? 0,
        z: option.rotation?.z ?? 0
    }

    const dracoCompressedGlb = await exportSceneForAR(vis)
    const data = {
        rotation,
        placement,
        scaleFactor,
        retentionTime,
        glb: Buffer.from(dracoCompressedGlb).toString("base64")
    }

    const request = new Request(`${option.baseUrl}/upload/`, {
        method: "POST",
        headers: new Headers({
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: "Bearer " + vis.cloud.token
        }),
        body: JSON.stringify(data),
        credentials: "include"
    })

    const response = await fetch(request)
    if (!response.ok) {
        throw response
    }
    const json = await response.json()
    return {
        link: json.link,
        qr: json.qr
    }
}

async function exportSceneForAR(vis: IVisualizationServer): Promise<any> {
    const scene = vis.cloud.graph().scene()

    const exporter = (await vis.useExtension(
        Constants.Extension.ThreeDExporter,
        vis.version,
        vis.cloud.token
    )) as IVisualizationExtension3DExporter

    return exporter
        .exportScene("glb", scene, { compress: true })
        .then((result: any) => result.content)
}
